import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Shield, Layers, Gauge, Package, Server,
  CheckCircle2, XCircle, AlertTriangle, ChevronDown,
  Clock, Zap, Eye, Lock, Bell, RotateCcw,
  TestTube, Code2, Hammer, Plus, Pencil, Trash2,
  Pause, Database, Search, X
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import TaskRunnerPanel from '@/components/feedback/TaskRunnerPanel';


/* ═══════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════ */

const TIER_CONFIG = {
  1: { label: 'KEEP', sublabel: 'always ask', color: 'text-red-400', bg: 'bg-red-500/8', border: 'border-red-500/20', dotColor: 'bg-red-400' },
  2: { label: 'BATCH', sublabel: 'checkpoint', color: 'text-amber-400', bg: 'bg-amber-500/8', border: 'border-amber-500/20', dotColor: 'bg-amber-400' },
  3: { label: 'NOTIFY', sublabel: 'do then tell', color: 'text-sky-400', bg: 'bg-sky-500/8', border: 'border-sky-500/20', dotColor: 'bg-sky-400' },
  4: { label: 'AUTO', sublabel: 'full autonomy', color: 'text-emerald-400', bg: 'bg-emerald-500/8', border: 'border-emerald-500/20', dotColor: 'bg-emerald-400' },
};

const STATUS_CONFIG = {
  deployed: { label: 'Deployed', className: 'bg-amber-500/15 text-amber-400/90 border-amber-500/20', icon: CheckCircle2 },
  installing: { label: 'Installing', className: 'bg-sky-500/15 text-sky-400 border-sky-500/20', icon: Clock },
  designed: { label: 'Designed', className: 'bg-slate-500/15 text-slate-400 border-slate-500/20', icon: Clock },
  retired: { label: 'Retired', className: 'bg-slate-600/15 text-slate-500 border-slate-600/20', icon: XCircle },
  paused: { label: 'Paused', className: 'bg-slate-500/15 text-slate-400 border-slate-500/20', icon: Pause },
};

const DECISION_BADGE = {
  ADD: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  UPDATE: 'bg-slate-400/15 text-slate-300 border-slate-400/20',
  SKIP: 'bg-slate-600/15 text-slate-500 border-slate-600/20',
  PARTIAL: 'bg-slate-500/15 text-slate-400 border-slate-500/20',
};

const LAYER_LABELS = {
  0: { label: 'Infrastructure / Governance', desc: 'Governs the stack: registry, evaluator, governor' },
  1: { label: 'Domain Knowledge', desc: 'WHAT you know: vessels, HexDB, operators, terminology' },
  2: { label: 'Cognitive Modes', desc: 'WHAT lens you use: founder, engineer, designer, QA' },
  3: { label: 'Execution Discipline', desc: 'HOW you build: TDD, worktrees, subagents, plans' },
};

const ASSET_TYPES = ['skill', 'bot', 'plugin', 'knowledge_doc', 'agent', 'robot', 'config'];
const ENVIRONMENTS = ['claude_ai', 'claude_code', 'compass', 'physical', 'multi'];
const STATUSES = ['deployed', 'installing', 'designed', 'retired', 'paused'];
const DECISIONS = ['ADD', 'UPDATE', 'SKIP', 'PARTIAL'];

function parseJSON(str, fallback = []) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}


/* ═══════════════════════════════════════════════════════════
   SEED DATA — from STACK-REGISTRY.md, EVALUATION-PROTOCOL.md,
   AUTONOMY-TIERS.md (2026-03-19)
   ═══════════════════════════════════════════════════════════ */

const SEED_ASSETS = [
  { name:'Superpowers', asset_type:'plugin', layer:3, environment:'claude_code', status:'deployed', source:'obra/superpowers',
    description:'Execution discipline layer — TDD, worktrees, subagents, plans, parallel agents, skill authoring.',
    capabilities:JSON.stringify(['brainstorming','using-git-worktrees','writing-plans','subagent-driven-development','executing-plans','test-driven-development','systematic-debugging','verification-before-completion','finishing-a-development-branch','requesting-code-review','receiving-code-review','dispatching-parallel-agents','writing-skills']),
    installed_via:'Claude Code plugin marketplace', directory:'~/.claude/plugins/cache/Superpowers/',
    hooks:'session-start hook (bootstrap injection)', rollback_path:'/plugin uninstall superpowers',
    autonomy_impact:'TDD + subagent two-stage review = automated gate for implementation decisions (enables Tier 3/4 autonomy)',
    added_date:'2026-03-17', last_verified:'2026-03-19' },
  { name:'gstack v2', asset_type:'skill', layer:2, environment:'multi', status:'deployed', source:'garrytan/gstack',
    description:'Cognitive modes layer — 21 slash commands for founder, engineer, designer, QA lenses. 12 skills also deployed as claude.ai user skills.',
    capabilities:JSON.stringify(['/office-hours','/plan-ceo-review','/plan-eng-review','/plan-design-review','/design-consultation','/design-review','/review','/ship','/browse','/qa','/qa-only','/qa-design-review','/setup-browser-cookies','/retro','/document-release','/investigate','/codex','/careful','/freeze','/guard','/unfreeze']),
    installed_via:'git clone + setup script (Claude Code) + zip upload (claude.ai)', directory:'~/.claude/skills/gstack/ (Code) + claude.ai skill storage (ai)',
    hooks:'none', rollback_path:'rm -rf ~/.claude/skills/gstack/ + remove gstack section from CLAUDE.md + delete claude.ai skills',
    autonomy_impact:'/review Fix-First + /qa regression tests + /ship coverage audit = stronger automated gates',
    added_date:'2026-03-15', last_verified:'2026-03-23' },
  { name:'REEF claude.ai Skills', asset_type:'skill', layer:1, environment:'claude_ai', status:'deployed', source:'REEF custom (7-skill suite)',
    description:'Domain knowledge skills for claude.ai — product challenge, paranoid audit, QA review, deploy checklist, plan review, commanders briefing, visual explainer.',
    capabilities:JSON.stringify(['product-challenge','paranoid-audit','qa-review','deploy-checklist','plan-review-mode','commanders-briefing','visual-explainer']),
    installed_via:'Settings → Capabilities → Skills (zip upload)', directory:'claude.ai skill storage (managed by platform)',
    hooks:'', rollback_path:'Delete individual skills in Settings → Capabilities',
    autonomy_impact:'None directly (human-invoked cognitive modes)',
    added_date:'2026-03-15', last_verified:'2026-03-23' },
  { name:'REEF Strategic & Governance Skills', asset_type:'skill', layer:2, environment:'claude_ai', status:'deployed', source:'REEF custom (3-skill suite)',
    description:'Strategic and governance cognitive lenses for claude.ai — build-vs-wait decision framework, multi-agent delegation audit, delegation architecture advisor.',
    capabilities:JSON.stringify(['strategic-horizon','delegation-audit','delegation-advisor']),
    installed_via:'Settings → Capabilities → Skills (zip upload)', directory:'claude.ai skill storage (managed by platform)',
    hooks:'', rollback_path:'Delete individual skills in Settings → Capabilities',
    autonomy_impact:'strategic-horizon informs Tier 1 build/wait decisions; delegation-audit scores agent architectures against Tomašev framework',
    added_date:'2026-03-23', last_verified:'2026-03-23' },
  { name:'REEF Claude Code Skills', asset_type:'skill', layer:1, environment:'claude_code', status:'designed', source:'REEF custom (planned)',
    description:'Domain knowledge skills for Claude Code — REEF context, HexDB schema, COMPASS bridge, SQL rules.',
    capabilities:JSON.stringify(['/reef-context','/hexdb-schema','/compass-bridge','/reef-sql-rules']),
    installed_via:'CLAUDE.md sections + skill files in repo', directory:'per-repo /.claude/skills/ or CLAUDE.md inline',
    hooks:'', rollback_path:'Remove skill files and CLAUDE.md sections',
    autonomy_impact:'Provides domain context to all Layer 2 and Layer 3 tools',
    added_date:'', last_verified:'' },
  { name:'COMPASS Armory', asset_type:'skill', layer:0, environment:'multi', status:'deployed', source:'REEF custom (3-skill meta-layer)',
    description:'Meta-layer infrastructure — stack registry, intake evaluator, autonomy governor.',
    capabilities:JSON.stringify(['stack-registry','intake-evaluator','autonomy-governor']),
    installed_via:'Skill upload (claude.ai) + CLAUDE.md sections (Claude Code)', directory:'claude.ai skill storage + CLAUDE.md',
    hooks:'', rollback_path:'Delete skills in claude.ai Settings; remove CLAUDE.md sections',
    autonomy_impact:'Meta-layer — governs which tools exist and how they interact',
    added_date:'2026-03-17', last_verified:'2026-03-19' },
  { name:'CLAUDE.md (per-repo)', asset_type:'config', layer:0, environment:'claude_code', status:'deployed', source:'REEF custom',
    description:'Per-repo configuration file — REEF terminology rules, gstack section, Superpowers integration, autonomy rules, priority chain.',
    capabilities:JSON.stringify(['REEF terminology rules','gstack section','Superpowers integration','autonomy rules','priority chain']),
    installed_via:'git commit', directory:'repo root',
    hooks:'Loaded at session start (highest priority)', rollback_path:'git checkout CLAUDE.md',
    autonomy_impact:'HIGHEST PRIORITY — overrides all skills and plugins',
    added_date:'2026-03-15', last_verified:'2026-03-19' },
  { name:'settings.json', asset_type:'config', layer:0, environment:'claude_code', status:'deployed', source:'REEF custom',
    description:'Per-repo or global settings — allowedTools for Tier 4 autonomy (pre-approved safe commands).',
    capabilities:JSON.stringify(['allowedTools for Tier 4 autonomy']),
    installed_via:'git commit or manual creation', directory:'.claude/settings.json (per-repo) or ~/.claude/settings.json (global)',
    hooks:'', rollback_path:'git checkout .claude/settings.json',
    autonomy_impact:'Pre-approves Tier 4 tool commands — no confirmation prompts',
    added_date:'2026-03-17', last_verified:'2026-03-19' },
];

const SEED_EVALUATIONS = [
  { eval_id:'EVAL-2026-03-17-superpowers', asset_name:'Superpowers (obra/superpowers)', eval_date:'2026-03-17',
    decision:'ADD', unique_count:7, complementary_count:4, redundant_count:0, conflicting_count:0,
    key_reason:'Entire execution discipline layer we didn\'t have. Explicit CLAUDE.md priority respect.',
    autonomy_impact:'TDD + subagent review enables Tier 3/4 for implementation decisions',
    steps_json:JSON.stringify([
      {step:1,label:'Fetch & Scan',detail:'13 skills scanned, 1 hook (session-start), plugin marketplace install'},
      {step:2,label:'Stack Registry Diff',detail:'7 UNIQUE, 4 COMPLEMENTARY, 0 REDUNDANT, 0 CONFLICTING'},
      {step:3,label:'Complementarity Matrix',detail:'brainstorming ↔ /plan-ceo-review (COMPLEMENTARY), writing-plans ↔ /plan-eng-review (COMPLEMENTARY)'},
      {step:4,label:'Installation Safety',detail:'CLEAR on 5/6 checks. CAUTION: session-start hook (no conflict with gstack).'},
      {step:5,label:'Autonomy Impact',detail:'TDD + subagent two-stage review = automated gate for implementation correctness'},
      {step:6,label:'Layering Plan',detail:'/plugin install superpowers@claude-plugins-official + CLAUDE.md priority chain update'},
      {step:7,label:'Stack Registry Update',detail:'Entry added to Layer 3 — Execution Discipline'},
    ]), evaluated_by:'manual', approval_status:'approved' },
  { eval_id:'EVAL-2026-03-19-gstack-v2', asset_name:'gstack v2 Update', eval_date:'2026-03-19',
    decision:'UPDATE', unique_count:7, complementary_count:14, redundant_count:0, conflicting_count:0,
    key_reason:'Design system lifecycle, doc automation, product reframing, self-updating. All unique or complementary.',
    autonomy_impact:'/review Fix-First + /qa regression tests + /ship coverage audit = stronger automated gates',
    steps_json:JSON.stringify([
      {step:1,label:'Fetch & Scan',detail:'7 new skills + 5 updated skills scanned'},
      {step:2,label:'Stack Registry Diff',detail:'7 new UNIQUE capabilities, 14 total COMPLEMENTARY, 0 REDUNDANT, 0 CONFLICTING'},
      {step:3,label:'Complementarity Matrix',detail:'/office-hours (UNIQUE), /design-consultation (UNIQUE), /document-release (UNIQUE), /investigate (UNIQUE)'},
      {step:4,label:'Installation Safety',detail:'CLEAR — update in place, same directory'},
      {step:5,label:'Autonomy Impact',detail:'Fix-First auto-fixes + regression test gen + coverage audit = Tier 2 review batches are smaller'},
      {step:6,label:'Layering Plan',detail:'gstack upgrade in-place + CLAUDE.md update for new skills'},
      {step:7,label:'Stack Registry Update',detail:'Layer 2 entry updated: 8 → 21 skills'},
    ]), evaluated_by:'manual', approval_status:'approved' },
];

const SEED_RULES = [
  {rule_id:'P1-01',phase:'Phase 1 — Product Direction',decision_text:'Should we build this feature at all?',tier:1,rationale:'Pure product judgment. No automated gate can replace this.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P1-02',phase:'Phase 1 — Product Direction',decision_text:'Is this solving the right user problem?',tier:1,rationale:'Problem framing requires domain expertise and strategic context.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P2-01',phase:'Phase 2 — Design & Architecture',decision_text:'Does this design spec look right?',tier:1,rationale:'Design validation requires understanding of system context.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P2-02',phase:'Phase 2 — Design & Architecture',decision_text:'Is the architecture sound?',tier:1,rationale:'Architecture decisions have the longest blast radius.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P2-03',phase:'Phase 2 — Design & Architecture',decision_text:'Approve the implementation plan?',tier:1,rationale:'Plan approval is the last gate before autonomous execution.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P3-01',phase:'Phase 3 — Environment Setup',decision_text:'Create a git worktree?',tier:4,rationale:'Zero blast radius. Worktrees are isolated by definition.',automated_gate:'Git worktree isolation',last_recalibrated:'2026-03-17'},
  {rule_id:'P3-02',phase:'Phase 3 — Environment Setup',decision_text:'Restore deps from lockfile?',tier:4,rationale:'Pinned versions, no new code. Easily reversible.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P3-03',phase:'Phase 3 — Environment Setup',decision_text:'Verify test baseline passes?',tier:4,rationale:'Read-only operation. No risk.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P3-04',phase:'Phase 3 — Environment Setup',decision_text:'Add a NEW dependency?',tier:2,rationale:'Supply chain risk. Requires Dependency Vet Card.',automated_gate:'Dependency Vet Card',last_recalibrated:'2026-03-17'},
  {rule_id:'P3-05',phase:'Phase 3 — Environment Setup',decision_text:'Add dependency with --force?',tier:1,rationale:'Bypasses safety checks. Highest supply chain risk.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-01',phase:'Phase 4 — Implementation',decision_text:'Write a failing test?',tier:4,rationale:'TDD RED phase. Within worktree. Tests are the spec.',automated_gate:'Superpowers TDD',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-02',phase:'Phase 4 — Implementation',decision_text:'Write implementation code?',tier:4,rationale:'TDD GREEN phase. Within worktree. Covered by subagent review.',automated_gate:'Superpowers TDD',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-03',phase:'Phase 4 — Implementation',decision_text:'Refactor after tests pass?',tier:4,rationale:'TDD REFACTOR phase. Tests still pass = safe.',automated_gate:'Superpowers TDD',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-04',phase:'Phase 4 — Implementation',decision_text:'Edit file X?',tier:4,rationale:'Classic rubber stamp. Covered by worktree isolation + TDD.',automated_gate:'Git worktree isolation',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-05',phase:'Phase 4 — Implementation',decision_text:'Run bash/terminal command?',tier:4,rationale:'Test runs, linting, git commits. Within worktree.',automated_gate:'Git worktree isolation',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-06',phase:'Phase 4 — Implementation',decision_text:'Proceed to next task?',tier:4,rationale:'Plan was already approved at Tier 1. Execution is autonomous.',automated_gate:'Superpowers subagent review',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-07',phase:'Phase 4 — Implementation',decision_text:'Subagent is BLOCKED',tier:1,rationale:'Requires human judgment to unblock.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-08',phase:'Phase 4 — Implementation',decision_text:'Subagent done with CONCERNS',tier:2,rationale:'Accumulate and present at checkpoint.',automated_gate:'Superpowers subagent review',last_recalibrated:'2026-03-17'},
  {rule_id:'P4-09',phase:'Phase 4 — Implementation',decision_text:'Tests failed after implementation',tier:2,rationale:'Accumulate with concerns, present at checkpoint.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P5-01',phase:'Phase 5 — Review & QA',decision_text:'Review paranoid code review findings?',tier:1,rationale:'Final quality gate. Human eyes on critical findings.',automated_gate:'gstack /review',last_recalibrated:'2026-03-17'},
  {rule_id:'P5-02',phase:'Phase 5 — Review & QA',decision_text:'Review QA test results?',tier:2,rationale:'Batch with review findings at review checkpoint.',automated_gate:'gstack /qa',last_recalibrated:'2026-03-17'},
  {rule_id:'P5-03',phase:'Phase 5 — Review & QA',decision_text:'Accept/reject specific findings?',tier:2,rationale:'Triage as a batch, not one-by-one.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P6-01',phase:'Phase 6 — Ship',decision_text:'Merge branch to main?',tier:1,rationale:'Hard to undo. Affects all downstream.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P6-02',phase:'Phase 6 — Ship',decision_text:'Create pull request?',tier:1,rationale:'Release gate. Human approval required.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P6-03',phase:'Phase 6 — Ship',decision_text:'Clean up worktree after merge?',tier:4,rationale:'Housekeeping. No risk.',automated_gate:'',last_recalibrated:'2026-03-17'},
  {rule_id:'P6-04',phase:'Phase 6 — Ship',decision_text:'Deploy to production?',tier:1,rationale:'Highest blast radius decision in the entire flow.',automated_gate:'',last_recalibrated:'2026-03-17'},
];


/* ═══════════════════════════════════════════════════════════
   SEED FUNCTION — ENG-3: idempotent (skips existing by name/rule_id)
   ═══════════════════════════════════════════════════════════ */

function SeedButton({ assets, evaluations, rules }) {
  const queryClient = useQueryClient();
  const [seeding, setSeeding] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const hasData = (assets?.length || 0) + (evaluations?.length || 0) + (rules?.length || 0) > 0;
  if (hasData || done) return null;

  const handleSeed = async () => {
    setSeeding(true);
    let created = { assets: 0, evals: 0, rules: 0 };
    let skipped = 0;
    try {
      /* Fetch existing names/ids for dedup */
      const existingAssetNames = new Set((assets || []).map(a => a.name));
      const existingEvalIds = new Set((evaluations || []).map(e => e.eval_id));
      const existingRuleIds = new Set((rules || []).map(r => r.rule_id));

      for (const a of SEED_ASSETS) {
        if (existingAssetNames.has(a.name)) { skipped++; continue; }
        await base44.entities.ArmoryAsset.create(a);
        created.assets++;
      }
      for (const e of SEED_EVALUATIONS) {
        if (existingEvalIds.has(e.eval_id)) { skipped++; continue; }
        await base44.entities.ArmoryEvaluation.create(e);
        created.evals++;
      }
      for (const r of SEED_RULES) {
        if (existingRuleIds.has(r.rule_id)) { skipped++; continue; }
        await base44.entities.ArmoryAutonomyRule.create(r);
        created.rules++;
      }

      queryClient.invalidateQueries({ queryKey: ['armoryAssets'] });
      queryClient.invalidateQueries({ queryKey: ['armoryEvaluations'] });
      queryClient.invalidateQueries({ queryKey: ['armoryRules'] });
      setDone(true);

      const total = created.assets + created.evals + created.rules;
      toast.success(`Seeded ${total} records`, {
        description: `${created.assets} assets, ${created.evals} evals, ${created.rules} rules` + (skipped > 0 ? ` (${skipped} skipped — already existed)` : ''),
      });
    } catch (err) {
      console.error('Seed failed:', err);
      toast.error('Seed failed', { description: err.message });
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="p-4 rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 flex items-center gap-4">
      <Database className="w-5 h-5 text-amber-500/70 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-300">No data yet</p>
        <p className="text-xs text-slate-500">Seed the registry with your current stack from the project knowledge docs.</p>
      </div>
      <Button size="sm" onClick={handleSeed} disabled={seeding} className="bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30">
        {seeding ? 'Seeding...' : 'Seed from docs'}
      </Button>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   ASSET DIALOG (Create / Edit)
   ═══════════════════════════════════════════════════════════ */

const EMPTY_ASSET = { name:'', asset_type:'skill', layer:0, environment:'claude_ai', status:'deployed', source:'', description:'', capabilities:'', installed_via:'', directory:'', hooks:'', rollback_path:'', autonomy_impact:'', added_date:'', last_verified:'' };

function AssetDialog({ open, onOpenChange, editData, onSave }) {
  const [form, setForm] = React.useState(EMPTY_ASSET);
  const [capText, setCapText] = React.useState('');

  React.useEffect(() => {
    if (editData) {
      setForm(editData);
      setCapText(parseJSON(editData.capabilities, []).join(', '));
    } else {
      setForm({...EMPTY_ASSET, added_date: new Date().toISOString().slice(0,10)});
      setCapText('');
    }
  }, [editData, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const caps = capText.split(',').map(s => s.trim()).filter(Boolean);
    onSave({ ...form, capabilities: JSON.stringify(caps) });
    onOpenChange(false);
  };

  const set = (k, v) => setForm(prev => ({...prev, [k]: v}));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="armory-armory-dialog" className="bg-slate-900 border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-slate-200">{editData ? 'Edit asset' : 'Register new asset'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-400 text-xs">Name</Label><Input data-testid="armory-armory-input" value={form.name} onChange={e=>set('name',e.target.value)} required className="bg-slate-800 border-slate-700 text-slate-200 mt-1" /></div>
            <div><Label className="text-slate-400 text-xs">Source</Label><Input data-testid="armory-bg-slate-800-border-slate-700-text-input" value={form.source} onChange={e=>set('source',e.target.value)} className="bg-slate-800 border-slate-700 text-slate-200 mt-1" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-slate-400 text-xs">Type</Label>
              <Select value={form.asset_type} onValueChange={v=>set('asset_type',v)}><SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">{ASSET_TYPES.map(t=><SelectItem key={t} value={t} className="text-slate-200">{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-slate-400 text-xs">Layer</Label>
              <Select value={String(form.layer)} onValueChange={v=>set('layer',Number(v))}><SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">{[0,1,2,3].map(l=><SelectItem key={l} value={String(l)} className="text-slate-200">L{l} — {LAYER_LABELS[l].label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-slate-400 text-xs">Environment</Label>
              <Select value={form.environment} onValueChange={v=>set('environment',v)}><SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">{ENVIRONMENTS.map(e=><SelectItem key={e} value={e} className="text-slate-200">{e}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-400 text-xs">Status</Label>
              <Select value={form.status} onValueChange={v=>set('status',v)}><SelectTrigger className="bg-slate-800 border-slate-700 text-slate-200 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">{STATUSES.map(s=><SelectItem key={s} value={s} className="text-slate-200">{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-slate-400 text-xs">Installed via</Label><Input data-testid="armory-text-slate-400-text-xs-input" value={form.installed_via} onChange={e=>set('installed_via',e.target.value)} className="bg-slate-800 border-slate-700 text-slate-200 mt-1" /></div>
          </div>
          <div><Label className="text-slate-400 text-xs">Description</Label><Textarea data-testid="armory-armory-textarea" value={form.description} onChange={e=>set('description',e.target.value)} rows={2} className="bg-slate-800 border-slate-700 text-slate-200 mt-1" /></div>
          <div><Label className="text-slate-400 text-xs">Capabilities (comma-separated)</Label><Textarea data-testid="armory-skill-one-skill-two-textarea" value={capText} onChange={e=>setCapText(e.target.value)} rows={2} className="bg-slate-800 border-slate-700 text-slate-200 mt-1 font-mono text-xs" placeholder="skill-one, skill-two, ..." /></div>
          <div><Label className="text-slate-400 text-xs">Directory</Label><Input data-testid="armory-bg-slate-800-border-slate-700-text-input-1" value={form.directory} onChange={e=>set('directory',e.target.value)} className="bg-slate-800 border-slate-700 text-slate-200 mt-1 font-mono text-xs" /></div>
          <div><Label className="text-slate-400 text-xs">Rollback path</Label><Textarea data-testid="armory-armory-textarea-1" value={form.rollback_path} onChange={e=>set('rollback_path',e.target.value)} rows={1} className="bg-slate-800 border-slate-700 text-slate-200 mt-1 font-mono text-xs" /></div>
          <div><Label className="text-slate-400 text-xs">Autonomy impact</Label><Textarea data-testid="armory-armory-textarea-2" value={form.autonomy_impact} onChange={e=>set('autonomy_impact',e.target.value)} rows={2} className="bg-slate-800 border-slate-700 text-slate-200 mt-1" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={()=>onOpenChange(false)} className="border-slate-700 text-slate-400">Cancel</Button>
            <Button type="submit" className="bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30">{editData ? 'Save' : 'Register'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ═══════════════════════════════════════════════════════════
   DISPLAY COMPONENTS
   ═══════════════════════════════════════════════════════════ */

/* DES-6: ChevronDown merged into card's main button click zone for keyboard a11y */
function ToolCard({ tool, onEdit, onDelete, onUpdateChecklist }) {
  const [expanded, setExpanded] = React.useState(false);
  const statusCfg = STATUS_CONFIG[tool.status] || STATUS_CONFIG.deployed;
  const StatusIcon = statusCfg.icon;
  const caps = parseJSON(tool.capabilities, []);
  const checklist = parseJSON(tool.install_checklist, []);
  const isInstalling = tool.status === 'installing' && checklist.length > 0;
  const checkedCount = checklist.filter(function(s) { return s.checked; }).length;
  const allChecked = isInstalling && checkedCount === checklist.length;

  const ENV_BADGE_COLORS = {
    claude_ai: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    claude_code: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    compass: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    multi: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  };

  const handleCheckStep = (stepId) => {
    const updated = checklist.map(function(s) { return s.id === stepId ? { ...s, checked: !s.checked } : s; });
    if (onUpdateChecklist) onUpdateChecklist(tool.id, updated);
  };

  const handleMarkDeployed = () => {
    if (onUpdateChecklist) onUpdateChecklist(tool.id, checklist, true);
  };

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.25}}>
      <div className="rounded-xl border border-slate-800/80 bg-slate-900/50 hover:border-slate-700/80 transition-colors duration-200">
        <div className="flex items-start">
          <button onClick={()=>setExpanded(!expanded)} className="flex-1 p-5 flex items-start gap-4 text-left min-w-0">
            <div className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700/50 flex items-center justify-center flex-shrink-0">
              <span className="text-amber-500/90 font-bold text-sm">L{tool.layer}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-[15px] font-semibold text-slate-200">{tool.name}</h3>
                <Badge variant="outline" className={`text-[11px] border ${statusCfg.className}`}><StatusIcon className="w-3 h-3 mr-1" />{statusCfg.label}</Badge>
                <Badge variant="outline" className="text-[11px] border-slate-700 text-slate-500">{tool.asset_type}</Badge>
              </div>
              <p className="text-sm text-slate-600 mt-1 font-mono">{tool.source}</p>
              {tool.environment && (
                <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-800/60 px-2.5 py-1 rounded-md border border-slate-700/40 mt-2">
                  <Server className="w-3 h-3 text-slate-600" /> {tool.environment}
                </span>
              )}
            </div>
            {/* DES-6: chevron inside the button for unified keyboard focus */}
            <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform duration-200 flex-shrink-0 mt-3 ${expanded ? 'rotate-180' : ''}`} />
          </button>
          <div className="flex items-center gap-1 p-3 flex-shrink-0">
            <button onClick={()=>onEdit(tool)} className="p-1.5 rounded-md hover:bg-slate-800 text-slate-600 hover:text-slate-400 transition-colors" aria-label={`Edit ${tool.name}`}><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={()=>onDelete(tool.id)} className="p-1.5 rounded-md hover:bg-slate-800 text-slate-600 hover:text-red-400 transition-colors" aria-label={`Delete ${tool.name}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        <AnimatePresence>
          {expanded && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.2}} className="overflow-hidden">
              <div className="px-5 pb-5 space-y-4 border-t border-slate-800/60 pt-4">
                {tool.description && <p className="text-sm text-slate-400 leading-relaxed">{tool.description}</p>}
                {caps.length > 0 && (
                  <div>
                    <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider mb-2">Capabilities · {caps.length}</p>
                    <div className="flex flex-wrap gap-1.5">{caps.map((c,i)=>(<span key={i} className="text-xs font-mono text-slate-400 bg-slate-800/50 px-2 py-0.5 rounded border border-slate-700/30">{c}</span>))}</div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {tool.hooks && <div><p className="text-[11px] text-slate-600 mb-0.5">Hooks</p><p className="text-slate-400 text-xs">{tool.hooks || 'None'}</p></div>}
                  {tool.installed_via && <div><p className="text-[11px] text-slate-600 mb-0.5">Install method</p><p className="text-slate-400 font-mono text-xs">{tool.installed_via}</p></div>}
                  {tool.directory && <div><p className="text-[11px] text-slate-600 mb-0.5">Directory</p><p className="text-slate-400 font-mono text-xs break-all">{tool.directory}</p></div>}
                  {tool.added_date && <div><p className="text-[11px] text-slate-600 mb-0.5">Added</p><p className="text-slate-400 text-xs">{tool.added_date}</p></div>}
                  {tool.last_verified && <div><p className="text-[11px] text-slate-600 mb-0.5">Last verified</p><p className="text-slate-400 text-xs">{tool.last_verified}</p></div>}
                </div>
                {tool.autonomy_impact && (
                  <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                    <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider mb-1">Autonomy impact</p>
                    <p className="text-sm text-slate-400">{tool.autonomy_impact}</p>
                  </div>
                )}
                {tool.rollback_path && (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-800/20 border border-slate-700/20">
                    <RotateCcw className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                    <div><p className="text-[11px] text-slate-500 font-medium">Rollback</p><p className="text-xs text-slate-500 font-mono">{tool.rollback_path}</p></div>
                  </div>
                )}

                {/* Install checklist — shown for "installing" status */}
                {isInstalling && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-medium text-amber-400/70 uppercase tracking-wider">Install progress</p>
                      <span className="text-[11px] text-slate-500" aria-live="polite">{checkedCount} / {checklist.length} steps</span>
                    </div>
                    <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500/60 rounded-full transition-all duration-300" style={{width: (checkedCount / checklist.length * 100) + '%'}} />
                    </div>
                    <div className="space-y-1">
                      {checklist.map(function(step) {
                        return (
                          <label key={step.id} className={`flex items-start gap-2.5 p-2 rounded-lg border cursor-pointer transition-colors ${step.checked ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-slate-800/25 border-slate-800/40 hover:border-slate-700/60'}`}>
                            <input data-testid="armory-armory-input-1" type="checkbox" checked={step.checked} onChange={function() { handleCheckStep(step.id); }}
                              className="mt-1 flex-shrink-0 accent-amber-500" aria-label={step.label} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex text-[9px] font-medium px-1.5 py-0.5 rounded border ${ENV_BADGE_COLORS[step.env] || 'border-slate-700 text-slate-500 bg-slate-800/50'}`}>{step.env}</span>
                                <span className={`text-xs ${step.checked ? 'text-slate-500 line-through' : 'text-slate-300'}`}>{step.label}</span>
                              </div>
                              {step.command && <p className={`text-[11px] font-mono mt-0.5 ${step.checked ? 'text-slate-600' : 'text-slate-500'}`}>{step.command}</p>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {allChecked && (
                      <Button data-testid="armory-mark-as-deployed-btn" size="sm" onClick={handleMarkDeployed} className="w-full bg-emerald-500/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/30 mt-1">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark as deployed
                      </Button>
                    )}
                  </div>
                )}

                {/* Collapsed checklist for deployed assets that went through install */}
                {tool.status === 'deployed' && checklist.length > 0 && (
                  <div className="flex items-center gap-2 text-[11px] text-emerald-500/60">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>Installed {tool.last_verified || tool.added_date} — {checklist.length}/{checklist.length} steps</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}


function EvaluationCard({ evaluation, index, onDelete }) {
  const [expanded, setExpanded] = React.useState(false);
  const badgeClass = DECISION_BADGE[evaluation.decision] || DECISION_BADGE.ADD;
  const steps = parseJSON(evaluation.steps_json, []);
  const matrix = [
    { label: 'Unique', count: evaluation.unique_count || 0 },
    { label: 'Complementary', count: evaluation.complementary_count || 0 },
    { label: 'Redundant', count: evaluation.redundant_count || 0 },
    { label: 'Conflicting', count: evaluation.conflicting_count || 0 },
  ];

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.25,delay:index*0.08}}>
      <div className="rounded-xl border border-slate-800/80 bg-slate-900/50 hover:border-slate-700/80 transition-colors duration-200">
        <div className="flex items-start">
          <button onClick={()=>setExpanded(!expanded)} className="flex-1 p-5 flex items-start gap-4 text-left min-w-0">
            <div className="flex flex-col items-center gap-1 flex-shrink-0 pt-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-500/80 ring-4 ring-amber-500/10" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-[15px] font-semibold text-slate-200">{evaluation.asset_name}</h3>
                <Badge variant="outline" className={`text-[11px] font-bold border ${badgeClass}`}>{evaluation.decision}</Badge>
              </div>
              <p className="text-xs text-slate-600 mt-1 font-mono">{evaluation.eval_date}</p>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{evaluation.key_reason}</p>
              <div className="flex items-center gap-5 mt-3">{matrix.map(m=>(
                <div key={m.label} className="flex items-baseline gap-1.5">
                  <span className={`text-lg font-bold ${m.count>0?'text-slate-200':'text-slate-700'}`}>{m.count}</span>
                  <span className="text-[11px] text-slate-600">{m.label}</span>
                </div>
              ))}</div>
            </div>
            {/* DES-6: chevron inside button */}
            <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform duration-200 flex-shrink-0 mt-3 ${expanded?'rotate-180':''}`} />
          </button>
          <div className="flex items-center gap-1 p-3 flex-shrink-0">
            <button onClick={()=>onDelete(evaluation.id)} className="p-1.5 rounded-md hover:bg-slate-800 text-slate-600 hover:text-red-400 transition-colors" aria-label={`Delete evaluation ${evaluation.asset_name}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        <AnimatePresence>
          {expanded && steps.length > 0 && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.2}} className="overflow-hidden">
              <div className="px-5 pb-5 border-t border-slate-800/60 pt-4">
                <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider mb-3">7-Step evaluation protocol</p>
                <div className="space-y-1.5">{steps.map(s=>(
                  <div key={s.step} className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-800/25 border border-slate-800/40">
                    <div className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0 mt-0.5"><span className="text-[10px] font-bold text-amber-500/80">{s.step}</span></div>
                    <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-300">{s.label}</p><p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.detail}</p></div>
                  </div>
                ))}</div>
                {evaluation.autonomy_impact && (
                  <div className="mt-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                    <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider mb-1">Autonomy impact</p>
                    <p className="text-sm text-slate-400">{evaluation.autonomy_impact}</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}


function AutonomyPhaseGroup({ phaseLabel, rules, phaseIndex }) {
  const [collapsed, setCollapsed] = React.useState(false);
  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:0.25,delay:phaseIndex*0.06}}>
      <div className="mb-5">
        <button onClick={()=>setCollapsed(!collapsed)} className="w-full flex items-center gap-2.5 mb-3 group text-left">
          <ChevronDown className={`w-3.5 h-3.5 text-slate-600 transition-transform ${collapsed?'-rotate-90':''}`} />
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{phaseLabel}</h3>
          <div className="h-px flex-1 bg-slate-800/60 group-hover:bg-slate-700/60 transition-colors" />
        </button>
        <AnimatePresence>
          {!collapsed && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.15}} className="overflow-hidden">
              <div className="space-y-1 pl-6">{rules.map(rule=>{
                const tierCfg = TIER_CONFIG[rule.tier] || TIER_CONFIG[1];
                return (
                  <TooltipProvider key={rule.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`flex items-center gap-3 p-3 rounded-lg border ${tierCfg.border} ${tierCfg.bg} cursor-default`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${tierCfg.dotColor} flex-shrink-0`} />
                          <span className="text-sm text-slate-300 flex-1 min-w-0">{rule.decision_text}</span>
                          <span className={`text-[11px] font-mono font-semibold ${tierCfg.color} flex-shrink-0`}>T{rule.tier} · {tierCfg.label}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs bg-slate-800 border-slate-700 text-slate-300"><p className="text-xs">{rule.rationale}</p></TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}


function StatCard({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="p-4 rounded-xl border border-slate-800/80 bg-slate-900/40">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700/50 flex items-center justify-center"><Icon className="w-[18px] h-[18px] text-amber-500/70" /></div>
        <div><p className="text-2xl font-bold text-amber-500/90">{value}</p><p className="text-[11px] text-slate-500">{label}</p></div>
      </div>
      {sublabel && <p className="text-[11px] text-slate-600 mt-2">{sublabel}</p>}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════ */

export default function Armory() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = React.useState('registry');
  const [assetDialogOpen, setAssetDialogOpen] = React.useState(false);
  const [editingAsset, setEditingAsset] = React.useState(null);

  /* ENG-2: AlertDialog state for delete confirmations */
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  /* DES-1: search/filter state */
  const [searchQuery, setSearchQuery] = React.useState(''); /* { type: 'asset'|'eval', id, label } */

  /* ─── QUERIES (safe — returns [] if entity not yet created in Base44) ─── */
  const { data: assets = [], isLoading: loadingAssets, isError: errorAssets } = useQuery({
    queryKey: ['armoryAssets'],
    queryFn: async () => { try { return await base44.entities.ArmoryAsset.list(); } catch (e) { console.warn('ArmoryAsset query failed:', e); return []; } },
    initialData: [], retry: false
  });
  const { data: evaluations = [], isLoading: loadingEvals, isError: errorEvals } = useQuery({
    queryKey: ['armoryEvaluations'],
    queryFn: async () => { try { return await base44.entities.ArmoryEvaluation.list(); } catch (e) { console.warn('ArmoryEvaluation query failed:', e); return []; } },
    initialData: [], retry: false
  });
  const { data: rules = [], isLoading: loadingRules, isError: errorRules } = useQuery({
    queryKey: ['armoryRules'],
    queryFn: async () => { try { return await base44.entities.ArmoryAutonomyRule.list(); } catch (e) { console.warn('ArmoryAutonomyRule query failed:', e); return []; } },
    initialData: [], retry: false
  });

  const entitiesMissing = errorAssets && errorEvals && errorRules;

  /* ─── MUTATIONS — ENG-4: toast on success/error ─── */
  const createAsset = useMutation({
    mutationFn: d => base44.entities.ArmoryAsset.create(d),
    onSuccess: (_, vars) => { queryClient.invalidateQueries({ queryKey: ['armoryAssets'] }); toast.success('Asset registered', { description: vars.name }); },
    onError: (err) => toast.error('Failed to register asset', { description: err.message }),
  });
  const updateAsset = useMutation({
    mutationFn: ({id,data}) => base44.entities.ArmoryAsset.update(id,data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['armoryAssets'] }); toast.success('Asset updated'); },
    onError: (err) => toast.error('Failed to update asset', { description: err.message }),
  });
  const deleteAsset = useMutation({
    mutationFn: id => base44.entities.ArmoryAsset.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['armoryAssets'] }); toast.success('Asset removed from registry'); },
    onError: (err) => toast.error('Failed to delete asset', { description: err.message }),
  });
  const deleteEval = useMutation({
    mutationFn: id => base44.entities.ArmoryEvaluation.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['armoryEvaluations'] }); toast.success('Evaluation deleted'); },
    onError: (err) => toast.error('Failed to delete evaluation', { description: err.message }),
  });

  const handleSaveAsset = (formData) => {
    if (editingAsset) {
      const { id, created_date, ...data } = formData;
      updateAsset.mutate({ id: editingAsset.id, data });
    } else {
      createAsset.mutate(formData);
    }
    setEditingAsset(null);
  };

  const handleEditAsset = (asset) => { setEditingAsset(asset); setAssetDialogOpen(true); };
  const handleNewAsset = () => { setEditingAsset(null); setAssetDialogOpen(true); };

  /* ENG-2: replace confirm() with AlertDialog */
  const handleDeleteAsset = (id) => {
    const asset = assets.find(a => a.id === id);
    setDeleteTarget({ type: 'asset', id, label: asset?.name || 'this asset' });
  };
  const handleDeleteEval = (id) => {
    const ev = evaluations.find(e => e.id === id);
    setDeleteTarget({ type: 'eval', id, label: ev?.asset_name || 'this evaluation' });
  };
  const confirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'asset') deleteAsset.mutate(deleteTarget.id);
    if (deleteTarget.type === 'eval') deleteEval.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  /* ─── INSTALL CHECKLIST HANDLER ─── */
  const handleUpdateChecklist = (assetId, updatedChecklist, markDeployed) => {
    var updateData = { install_checklist: JSON.stringify(updatedChecklist) };
    if (markDeployed) {
      updateData.status = 'deployed';
      updateData.last_verified = new Date().toISOString().slice(0, 10);
    }
    updateAsset.mutate({ id: assetId, data: updateData });
  };

  /* ─── COMPUTED (memoized) ─── */
  const deployedCount = React.useMemo(() => assets.filter(a => a.status === 'deployed').length, [assets]);
  const installingAssets = React.useMemo(() => assets.filter(a => a.status === 'installing'), [assets]);
  const totalSkills = React.useMemo(() => assets.reduce((s, a) => s + parseJSON(a.capabilities, []).length, 0), [assets]);
  const sortedEvals = React.useMemo(() => [...evaluations].sort((a, b) => (b.eval_date || '').localeCompare(a.eval_date || '')), [evaluations]);

  const { tierCounts, totalDecisions, interruptReduction } = React.useMemo(() => {
    const tc = { 1:0, 2:0, 3:0, 4:0 };
    rules.forEach(r => { if (tc[r.tier] !== undefined) tc[r.tier]++; });
    const td = rules.length || 1;
    const ir = Math.round(((tc[4] + tc[3]*0.5 + tc[2]*0.7) / td) * 100);
    return { tierCounts: tc, totalDecisions: td, interruptReduction: ir };
  }, [rules]);

  /* DES-1: filter assets by search query */
  const filteredAssets = React.useMemo(() => {
    if (!searchQuery.trim()) return assets;
    const q = searchQuery.toLowerCase();
    return assets.filter(a => {
      const caps = parseJSON(a.capabilities, []).join(' ').toLowerCase();
      return (a.name || '').toLowerCase().includes(q)
        || (a.description || '').toLowerCase().includes(q)
        || (a.source || '').toLowerCase().includes(q)
        || (a.environment || '').toLowerCase().includes(q)
        || (a.asset_type || '').toLowerCase().includes(q)
        || caps.includes(q);
    });
  }, [assets, searchQuery]);

  /* Group filtered assets by layer (descending: L3→L0) */
  const assetsByLayer = React.useMemo(() =>
    [3,2,1,0].map(layer => ({
      layer,
      ...LAYER_LABELS[layer],
      tools: filteredAssets.filter(a => a.layer === layer),
    })).filter(g => g.tools.length > 0),
  [filteredAssets]);

  /* Group rules by phase */
  const { rulePhases, phaseMap } = React.useMemo(() => {
    const phases = [];
    const map = {};
    rules.forEach(r => {
      if (!map[r.phase]) { map[r.phase] = []; phases.push(r.phase); }
      map[r.phase].push(r);
    });
    return { rulePhases: phases, phaseMap: map };
  }, [rules]);

  const isLoading = loadingAssets || loadingEvals || loadingRules;

  return (
    <div className="space-y-6">
      {/* ─── HEADER ─── */}
      <motion.div initial={{opacity:0,y:-12}} animate={{opacity:1,y:0}} className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-slate-800 border border-slate-700/60 flex items-center justify-center shadow-lg"><Hammer className="w-6 h-6 text-amber-500" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-100 tracking-tight">COMPASS Armory</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tool stack lifecycle management — registry, evaluation, and autonomy governance</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[11px] text-amber-500/70 bg-amber-500/8 px-2 py-0.5 rounded border border-amber-500/15 font-medium">Commander only</span>
            <span className="text-[11px] text-slate-500 bg-slate-800/60 px-2 py-0.5 rounded border border-slate-700/40 font-mono">P1 — entity-backed</span>
          </div>
        </div>
      </motion.div>

      {/* ─── SETUP NEEDED (entities not yet created in Base44) ─── */}
      {entitiesMissing && (
        <div className="p-6 rounded-xl border border-dashed border-amber-500/30 bg-amber-500/5 space-y-3">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500/80 flex-shrink-0" />
            <h2 className="text-sm font-semibold text-slate-200">Armory entities not found</h2>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            The four Armory entities have not been created in Base44 yet. Before using this page, create these entities in the Base44 entity editor:
          </p>
          <div className="flex flex-wrap gap-2">
            {['ArmoryAsset','ArmoryEvaluation','ArmoryAutonomyRule','ArmoryKnowledgeDoc'].map(name => (
              <span key={name} className="text-xs font-mono text-amber-400/80 bg-slate-800/60 px-2.5 py-1 rounded border border-slate-700/40">{name}</span>
            ))}
          </div>
          <p className="text-xs text-slate-500">Use the Armory Entity Schema Guide (delivered with this build) for field definitions. Once created, refresh this page.</p>
        </div>
      )}

      {/* ─── SEED BUTTON (auto-hides when data exists) ─── */}
      {!entitiesMissing && <SeedButton assets={assets} evaluations={evaluations} rules={rules} />}

      {/* ─── STATS ─── */}
      {!isLoading && !entitiesMissing && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.08}} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Package} label="Assets registered" value={assets.length} sublabel={`${deployedCount} deployed`} />
          <StatCard icon={Code2} label="Total capabilities" value={totalSkills} sublabel="Across all layers" />
          <StatCard icon={TestTube} label="Evaluations" value={evaluations.length} sublabel={`${evaluations.filter(e=>e.decision==='SKIP').length} rejected`} />
          <StatCard icon={Gauge} label="Interrupt reduction" value={rules.length > 0 ? `~${interruptReduction}%` : '—'} sublabel={`${tierCounts[4]} auto / ${tierCounts[1]} keep`} />
        </motion.div>
      )}

      {/* ─── ARCHITECTURE STACK ─── */}
      {assetsByLayer.length > 0 && (
        <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.12}}>
          <div className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-4">
            <p className="text-[11px] font-medium text-slate-600 uppercase tracking-wider mb-3">Architecture stack</p>
            <div className="space-y-1.5">{assetsByLayer.map(g => {
              const skillCount = g.tools.reduce((s,t)=>s+parseJSON(t.capabilities,[]).length,0);
              const opacity = [0.4,0.55,0.7,0.9][g.layer];
              return (
                <div key={g.layer} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
                  <div className="w-8 h-8 rounded-md bg-slate-800 border border-slate-700/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-500 font-bold text-xs" style={{opacity}}>L{g.layer}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-300" style={{opacity:0.6+opacity*0.4}}>{g.label}</p>
                    <p className="text-xs text-slate-600">{g.desc}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-[11px] text-slate-500">
                    <span>{g.tools.length} asset{g.tools.length!==1?'s':''}</span>
                    <span className="text-slate-700">·</span>
                    <span>{skillCount} capabilities</span>
                  </div>
                </div>
              );
            })}</div>
          </div>
        </motion.div>
      )}

      {/* ─── TABS ─── */}
      {!entitiesMissing && (
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-900/60 border border-slate-800 p-1 w-full justify-start gap-1">
          <TabsTrigger value="registry" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 data-[state=active]:border-amber-500/20 data-[state=active]:border px-4 text-slate-500">
            <Layers className="w-4 h-4 mr-2" />Stack Registry
          </TabsTrigger>
          <TabsTrigger value="evaluations" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 data-[state=active]:border-amber-500/20 data-[state=active]:border px-4 text-slate-500">
            <TestTube className="w-4 h-4 mr-2" />Evaluation Log
          </TabsTrigger>
          <TabsTrigger value="autonomy" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 data-[state=active]:border-amber-500/20 data-[state=active]:border px-4 text-slate-500">
            <Gauge className="w-4 h-4 mr-2" />Autonomy Governor
          </TabsTrigger>
          <TabsTrigger value="taskrunner" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400 data-[state=active]:border-amber-500/20 data-[state=active]:border px-4 text-slate-500">
            <Zap className="w-4 h-4 mr-2" />Task Runner
          </TabsTrigger>
        </TabsList>

        {/* ═══ TAB 1: STACK REGISTRY ═══ */}
        <TabsContent value="registry" className="mt-6 space-y-6">
          {/* Installing status alert strip — DES Pass 1 */}
          {installingAssets.length > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-xl border border-amber-500/25 bg-amber-500/5">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
              <p className="text-sm text-amber-400/90 flex-1">
                <span className="font-semibold">{installingAssets.length} tool{installingAssets.length !== 1 ? 's' : ''} installing:</span>{' '}
                {installingAssets.map(function(a) {
                  var cl = parseJSON(a.install_checklist, []);
                  var done = cl.filter(function(s) { return s.checked; }).length;
                  return a.name + ' (' + done + '/' + cl.length + ')';
                }).join(', ')}
              </p>
            </div>
          )}

          {/* DES-1: search/filter bar */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
              <Input data-testid="armory-armory-input-2"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Filter assets by name, capability, environment..."
                className="bg-slate-800/60 border-slate-700/60 text-slate-200 pl-10 pr-8 h-9 text-sm placeholder:text-slate-600"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Button data-testid="armory-register-asset-btn" size="sm" onClick={handleNewAsset} className="bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30 h-9">
              <Plus className="w-4 h-4 mr-1" /> Register asset
            </Button>
          </div>

          {/* Search result count when filtering */}
          {searchQuery && (
            <p className="text-xs text-slate-500">
              {filteredAssets.length} of {assets.length} assets match "<span className="text-slate-400">{searchQuery}</span>"
            </p>
          )}

          <div className="space-y-8">

          {assetsByLayer.map(group => (
            <div key={group.layer}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 rounded-md bg-slate-800 border border-slate-700/40 flex items-center justify-center">
                  <span className="text-amber-500/80 font-bold text-xs">L{group.layer}</span>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-200">Layer {group.layer} — {group.label}</h2>
                  <p className="text-xs text-slate-600">{group.desc}</p>
                </div>
              </div>
              <div className="space-y-2.5">
                {group.tools.map(tool => (
                  <ToolCard key={tool.id} tool={tool} onEdit={handleEditAsset} onDelete={handleDeleteAsset} onUpdateChecklist={handleUpdateChecklist} />
                ))}
              </div>
            </div>
          ))}
          </div>

          {assets.length === 0 && !isLoading && (
            <div className="text-center py-12"><p className="text-slate-600">No assets registered yet. Seed from docs or register one manually.</p></div>
          )}
          {assets.length > 0 && filteredAssets.length === 0 && searchQuery && (
            <div className="text-center py-12"><p className="text-slate-600">No assets match your search.</p></div>
          )}
        </TabsContent>

        {/* ═══ TAB 2: EVALUATION LOG ═══ */}
        <TabsContent value="evaluations" className="mt-6 space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <TestTube className="w-4 h-4 text-amber-500/70" />
            <h2 className="text-base font-semibold text-slate-200">Intake evaluations</h2>
            <span className="text-[11px] text-slate-500 bg-slate-800/60 px-2 py-0.5 rounded border border-slate-700/40 font-mono">7-step protocol</span>
          </div>
          <div className="space-y-2.5">
            {sortedEvals.map((ev, i) => (<EvaluationCard key={ev.id} evaluation={ev} index={i} onDelete={handleDeleteEval} />))}
          </div>
          {evaluations.length === 0 && !isLoading && (
            <div className="text-center py-12"><p className="text-slate-600">No evaluations recorded yet. Run <span className="font-mono text-amber-500/50">intake-evaluator</span> in claude.ai.</p></div>
          )}
        </TabsContent>

        {/* ═══ TAB 3: AUTONOMY GOVERNOR ═══ */}
        <TabsContent value="autonomy" className="mt-6 space-y-6">
          {/* Tier legend */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(TIER_CONFIG).map(([tier, cfg]) => (
              <div key={tier} className={`p-3 rounded-xl border ${cfg.border} ${cfg.bg}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${cfg.dotColor}`} />
                  <span className={`text-sm font-semibold ${cfg.color}`}>T{tier} · {cfg.label}</span>
                </div>
                <p className="text-[11px] text-slate-600 mt-0.5 ml-4">{cfg.sublabel}</p>
                <div className="mt-2 ml-4">
                  <span className="text-xl font-bold text-slate-300">{tierCounts[tier]}</span>
                  <span className="text-[11px] text-slate-600 ml-1">({Math.round((tierCounts[tier]/totalDecisions)*100)}%)</span>
                </div>
              </div>
            ))}
          </div>

          {/* Phase groups */}
          <div>{rulePhases.map((phase, i) => (
            <AutonomyPhaseGroup key={phase} phaseLabel={phase} rules={phaseMap[phase]} phaseIndex={i} />
          ))}</div>

          {rules.length === 0 && !isLoading && (
            <div className="text-center py-12"><p className="text-slate-600">No autonomy rules defined yet. Seed from docs to populate the decision matrix.</p></div>
          )}
        </TabsContent>

        {/* ═══ TAB 4: TASK RUNNER ═══ */}
        <TabsContent value="taskrunner" className="mt-6">
          <TaskRunnerPanel />
        </TabsContent>
      </Tabs>
      )}

      {/* ─── ASSET DIALOG ─── */}
      {!entitiesMissing && <AssetDialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen} editData={editingAsset} onSave={handleSaveAsset} />}

      {/* ─── ENG-2: DELETE CONFIRMATION DIALOG ─── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-200">
              {deleteTarget?.type === 'asset' ? 'Remove asset from registry?' : 'Delete this evaluation?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This will permanently delete <span className="font-semibold text-slate-300">{deleteTarget?.label}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-700 text-slate-400 hover:bg-slate-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}